import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSettings } from '../contexts/SettingsContext.tsx';
import {
  HomeIcon,
  BuildingLibraryIcon,
  TruckIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  CreditCardIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  SwatchIcon,
  BanknotesIcon,
  ClockIcon,
  DocumentCheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  HomeModernIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import BoatIcon from './icons/BoatIcon.tsx';

const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

type NavItem = {
  name: string;
  href: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  resource?: string;
  permission?: string;
  match?: (path: string) => boolean;
};

type NavGroup =
  | { kind: 'items'; label: string; items: NavItem[] }
  | {
      kind: 'submenu';
      label: string;
      name: string;
      icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
      children: NavItem[];
      resource?: string;
    };

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  variant?: 'desktop' | 'drawer';
}

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileClose,
  variant = 'desktop',
}) => {
  const location = useLocation();
  const { logout, user, hasPermission, hasResourceAccess } = useAuth();
  const { settings } = useSettings();
  const contasActive =
    location.pathname.includes('/financeiro/contas') ||
    location.pathname.includes('/financeiro/contas-a-pagar') ||
    location.pathname.includes('/financeiro/contas-a-receber');
  const [contasOpen, setContasOpen] = useState(contasActive);

  useEffect(() => {
    if (contasActive) setContasOpen(true);
  }, [contasActive]);

  const canAccess = (item: { permission?: string; resource?: string }) => {
    if (item.permission) return hasPermission(item.permission);
    if (item.resource) return hasResourceAccess(item.resource);
    return true;
  };

  const groups: NavGroup[] = [
    {
      kind: 'items',
      label: 'Patrimônio',
      items: [
        {
          name: 'Início',
          href: '/dashboard',
          icon: HomeIcon,
          resource: 'dashboard',
          match: p => p === '/' || p === '/dashboard',
        },
        { name: 'Bens e ativos', href: '/assets', icon: BuildingLibraryIcon, resource: 'assets' },
        { name: 'Frota', href: '/patrimonio/frota', icon: TruckIcon, resource: 'assets' },
        { name: 'Estoque p/ venda', href: '/vehicles', icon: ArchiveBoxIcon, resource: 'assets' },
        { name: 'Aluguéis', href: '/rentals', icon: HomeModernIcon, resource: 'assets' },
        { name: 'Embarcações', href: '/vessels', icon: BoatIcon, resource: 'assets' },
        { name: 'Aeronaves', href: '/aircrafts', icon: PaperAirplaneIcon, resource: 'assets' },
        { name: 'Investimentos', href: '/investments', icon: BanknotesIcon, resource: 'investments' },
        { name: 'Filantropia', href: '/philanthropies', icon: HeartIcon, resource: 'investments' },
        { name: 'Empresas', href: '/companies', icon: BuildingOfficeIcon, resource: 'companies' },
      ],
    },
    {
      kind: 'items',
      label: 'Financeiro',
      items: [
        {
          name: 'Fluxo de caixa',
          href: '/financeiro/fluxo-de-caixa',
          icon: ChartBarIcon,
          resource: 'debts',
        },
      ],
    },
  ];

  const tab = new URLSearchParams(location.search).get('tab');
  const contasChildren: NavItem[] = [
    {
      name: 'A pagar',
      href: '/financeiro/contas?tab=pagar',
      resource: 'debts',
      match: p =>
        p.includes('contas-a-pagar') ||
        p.includes('/contas/pagar') ||
        (p.startsWith('/financeiro/contas') && tab !== 'receber'),
    },
    {
      name: 'A receber',
      href: '/financeiro/contas?tab=receber',
      resource: 'receivables',
      match: p =>
        p.includes('contas-a-receber') ||
        p.includes('/contas/receber') ||
        (p.startsWith('/financeiro/contas') && tab === 'receber'),
    },
  ];

  const afterFinanceiro: NavGroup[] = [
    {
      kind: 'items',
      label: '',
      items: [
        { name: 'Dívidas', href: '/financeiro/dividas', icon: CreditCardIcon, resource: 'debts' },
        {
          name: 'Lançamentos auto.',
          href: '/financeiro/lancamentos-automaticos',
          icon: ClockIcon,
          resource: 'debts',
        },
        {
          name: 'Conciliação OFX',
          href: '/financeiro/conciliacao-ofx',
          icon: DocumentCheckIcon,
          resource: 'debts',
        },
      ],
    },
    {
      kind: 'items',
      label: 'Relatórios',
      items: [
        { name: 'Resumos', href: '/new-reports', icon: DocumentChartBarIcon, resource: 'reports' },
      ],
    },
    {
      kind: 'items',
      label: 'Config',
      items: [
        { name: 'Usuários', href: '/users', icon: UserGroupIcon, permission: 'users.read' },
        { name: 'Identidade visual', href: '/whitelabel', icon: SwatchIcon, resource: 'whitelabel' },
        {
          name: 'Preferências',
          href: '/settings/security',
          icon: Cog6ToothIcon,
          match: (p: string) => p.startsWith('/settings/security'),
        },
      ],
    },
  ];

  const brandName = settings?.companyName || 'Meu Patrimônio';
  const brandMark = brandName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase() || 'MP';

  const renderItem = (item: NavItem, opts?: { nested?: boolean }) => {
    if (!canAccess(item)) return null;
    const Icon = item.icon || BanknotesIcon;
    const isActive = item.match
      ? item.match(location.pathname)
      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    return (
      <NavLink
        key={item.name + item.href}
        to={item.href}
        onClick={() => onMobileClose?.()}
        className={`nav-item${isActive ? ' active' : ''}`}
        style={opts?.nested ? undefined : undefined}
      >
        {!opts?.nested && <Icon className="nav-icon" aria-hidden="true" />}
        {item.name}
      </NavLink>
    );
  };

  const navBody = (
    <>
      <div className="brand">
        {settings?.logoUrl ? (
          <img
            src={
              settings.logoUrl.startsWith('/uploads/')
                ? getBackendUrl(settings.logoUrl)
                : settings.logoUrl
            }
            alt={brandName}
            style={{ height: 32, width: 'auto', marginRight: 8 }}
          />
        ) : (
          <div className="brand-mark">{brandMark}</div>
        )}
        <div style={{ minWidth: 0 }}>
          <div className="brand-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {brandName}
          </div>
        </div>
        {variant === 'drawer' && (
          <button type="button" className="drawer-close" onClick={onMobileClose} aria-label="Fechar">
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>

      <nav className="nav">
        {groups.map(group => {
          if (group.kind !== 'items') return null;
          const visible = group.items.filter(canAccess);
          if (!visible.length) return null;
          return (
            <div className="nav-section" key={group.label || 'g'}>
              {group.label && <div className="nav-layer-label">{group.label}</div>}
              {visible.map(item => renderItem(item))}
              {group.label === 'Financeiro' && (
                <>
                  {(canAccess({ resource: 'debts' }) || canAccess({ resource: 'receivables' })) && (
                    <div className="nav-group">
                      <button
                        type="button"
                        className={`nav-group-parent${contasOpen ? ' open' : ''}${contasActive ? ' active-section' : ''}`}
                        onClick={() => setContasOpen(o => !o)}
                        style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer' }}
                      >
                        <BanknotesIcon className="nav-icon" />
                        Contas
                        <span className="nav-chevron">
                          <ChevronDownIcon
                            style={{
                              width: 14,
                              height: 14,
                              transform: contasOpen ? 'rotate(180deg)' : undefined,
                            }}
                          />
                        </span>
                      </button>
                      {contasOpen && (
                        <div className="nav-sub">
                          {contasChildren.map(c => renderItem(c, { nested: true }))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {afterFinanceiro.map(group => {
          if (group.kind !== 'items') return null;
          const visible = group.items.filter(canAccess);
          if (!visible.length) return null;
          return (
            <div className="nav-section" key={group.label || visible[0].name}>
              {group.label && <div className="nav-layer-label">{group.label}</div>}
              {visible.map(item => renderItem(item))}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          type="button"
          onClick={() => logout()}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit',
          }}
        >
          {user?.name || 'Usuário'} · Sair
        </button>
      </div>
    </>
  );

  if (variant === 'drawer') {
    if (!mobileOpen) return null;
    return (
      <>
        <div
          className="drawer-backdrop"
          onClick={onMobileClose}
          onKeyDown={e => e.key === 'Enter' && onMobileClose?.()}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu"
        />
        <aside className="drawer" aria-label="Menu">
          {navBody}
        </aside>
      </>
    );
  }

  return (
    <aside className="sidebar" aria-label="Navegação principal">
      {navBody}
    </aside>
  );
};

export default Sidebar;
